import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * A modal dialog, built on the native `<dialog>` element.
 *
 * `showModal()` gives focus trapping, Escape-to-close, inert background content
 * and top-layer stacking for free - all four of which are fiddly to get right by
 * hand and all four of which are usually got wrong. The cost is a ref and an
 * effect to keep the element in step with the `open` prop, which is a much
 * smaller surface than a hand-rolled overlay.
 *
 * Two details that are easy to miss:
 *
 * Escape is intercepted rather than allowed to close the element - see
 * `handleCancel`. Every way out goes through `onClose`, which is what lets one
 * `useDiscardGuard` cover all of them.
 *
 * The backdrop click is checked against the dialog's own bounds. `<dialog>`
 * reports clicks on its backdrop as clicks on the dialog itself, so comparing
 * against `event.target` alone would close it on every click inside the form.
 *
 * ── The page behind is locked, and `showModal` does not do it ───────────────
 *
 * The top layer makes the document inert, which stops clicks and focus. It does
 * not stop the wheel: a dialog narrower than the viewport leaves backdrop down
 * both sides, and a scroll started there ran the page under the dialog instead
 * of the dialog's own body - most visible on the wide media and history
 * dialogs, which are the ones with something to scroll.
 *
 * So the lock is ours, and it is counted rather than a boolean: a confirmation
 * opens over a form modal, and the inner one closing must not hand the page
 * back while the outer one is still up.
 */

/** How many modals are open. The lock lifts on the last one, not the first. */
let locks = 0;
export function Modal({
  open,
  onClose,
  title,
  footer,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  /** A node, not a string: the media dialog sets its position back from its
      heading, and `aria-labelledby` reads whatever is inside the `h2`. */
  title: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const ref = React.useRef<HTMLDialogElement>(null);
  // Generated rather than a literal: a confirmation can open over a form modal,
  // and two elements sharing `id="modal-title"` means the second dialog is
  // announced with the first one's name.
  const titleId = React.useId();

  React.useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  React.useEffect(() => {
    if (!open) return;

    locks += 1;
    document.documentElement.classList.add("scroll-locked");
    return () => {
      locks -= 1;
      if (locks === 0) document.documentElement.classList.remove("scroll-locked");
    };
  }, [open]);

  function handleBackdropClick(event: React.MouseEvent<HTMLDialogElement>) {
    const element = ref.current;
    if (!element || event.target !== element) return;

    const box = element.getBoundingClientRect();
    const inside =
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom;

    if (!inside) onClose();
  }

  /**
   * Escape, routed through `onClose` instead of letting the dialog close itself.
   *
   * **This is what makes a discard guard possible.** Escape fires `cancel` and
   * then closes the element natively, so a guard wired to `onClose` would be
   * asked "discard your changes?" over a panel that had already gone - and
   * answering "keep editing" would leave React believing it was open while the
   * element was shut, with nothing to re-open it.
   *
   * Preventing the default keeps the element open and hands the decision to the
   * owner, which is the same path Cancel, the close button and the backdrop take.
   * The effect above is then the only thing that ever closes it.
   */
  function handleCancel(event: React.SyntheticEvent<HTMLDialogElement>) {
    event.preventDefault();
    onClose();
  }

  return (
    <dialog
      ref={ref}
      onCancel={handleCancel}
      onClick={handleBackdropClick}
      aria-labelledby={titleId}
      className={cn("modal", className)}
    >
      <div className="modal__inner">
        <header className="modal__header">
          <h2 id={titleId} className="modal__title">
            {title}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="modal__close">
            <X />
          </button>
        </header>

        <div className="modal__body">{children}</div>

        {footer && <footer className="modal__footer">{footer}</footer>}
      </div>
    </dialog>
  );
}
